import { CommonModule, formatCurrency } from "@angular/common";
import { Component, computed, inject, Input, LOCALE_ID, OnInit, signal } from "@angular/core";
import { FlexLayoutModule, FlexModule } from "@ngbracket/ngx-layout";
import { BusyIndicatorComponent } from "@tylertech/common-components-module";
import { isErroredExecutionResult, isHttpResponseError, ToastService } from "@tylertech/core-module";
import { ForgeIconButtonModule, ForgeIconModule, ForgeSkeletonModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { getLoggerFor } from "@tylertech/logging";
import { MetadataActionService, RepositoryFormData } from "@tylertech/metadata-module";
import { IChartCriteria, IInBalanceCheck } from "@tylertech/tyler-api-types";
import { InBalanceService } from "../../services";
import { InBalanceChartComponent } from "./inBalanceChart.component";

@Component({
    selector: "in-balance-check",
    imports: [
        BusyIndicatorComponent,
        CommonModule,
        FlexModule,
        FlexLayoutModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeSkeletonModule,
        ForgeTooltipModule,
        InBalanceChartComponent,
    ],
    templateUrl: "./inBalanceCheck.tmpl.html",
    styleUrls: ["./inBalanceCheck.component.scss"],
    host: {
        style: `display: flex; flex-direction: column; justify-contents: center`
    }
})
export class InBalanceCheckComponent implements OnInit
{
    private readonly log = getLoggerFor("InBalanceCheckComponent");

    private readonly $toastService = inject(ToastService);
    private readonly $metadataActionService = inject(MetadataActionService);
    private readonly $inBalanceService = inject(InBalanceService);
    private readonly locale = inject(LOCALE_ID);

    @Input() provider: string;
    @Input() criteria: IChartCriteria;

    readonly loaded = signal<boolean>(false);
    reloading: boolean = false;
    readonly check = signal<IInBalanceCheck | undefined>(undefined);
    readonly showCheck = computed(() =>
    {
        const check = this.check();
        return !!check && check.IsHidden !== true;
    });

    readonly hasAction = computed(() => !!this.check()?.ViewAction);

    readonly name = computed(() => this.check()?.Name ?? "");

    private readonly hasVariance = computed(() => !!this.check()?.Variance);

    readonly variance = computed(() =>
    {
        const check = this.check();
        if (!check?.Variance)
        {
            return "";
        }

        const variance = check.IsCurrency
            ? formatCurrency(check.Variance, this.locale, "$")
            : check.Variance;
        return `Variance: ${variance}`;
    });

    readonly icon = computed(() =>
    {
        return this.hasVariance()
            ? "error"
            : "check_circle";
    });

    readonly iconStyle = computed(() =>
    {
        return {
            'color': this.hasVariance()
                ? "var(--forge-theme-warning)"
                : "var(--forge-theme-success)"
        };
    });


    ngOnInit(): void
    {
        this.initialize(this.criteria, true);
    }

    initialize(criteria: IChartCriteria, initial: boolean): void
    {
        criteria = { ...criteria, ProviderId: this.provider, Parameters: (criteria.Parameters ?? []).map(p => ({ ...p })) };
        if (!initial)
        {
            this.reloading = true;
            criteria.Parameters.forEach(p =>
            {
                if (!p.AdditionalAttributes)
                    p.AdditionalAttributes = {};
                p.AdditionalAttributes["SkipCache"] = true;
            });
        }

        this.$inBalanceService.GetInBalanceCheck(criteria)
            .then(
                (result: IInBalanceCheck) =>
                {
                    this.check.set(result);
                },
                (error: unknown) =>
                {
                    if (isErroredExecutionResult(error))
                    {
                        this.$toastService.showToast(error.ErrorMessage);
                    }
                    else if (isHttpResponseError(error))
                    {
                        this.$toastService.showToast(error.error.Message);
                    }
                    else
                    {
                        this.$toastService.showToast("An Error occurred.");
                        this.log.error(`An Error occurred: ${error}`, new Error(`An Error occurred.`));
                    }
                })
            .finally(() =>
            {
                this.loaded.set(true);
                this.reloading = false;
            });
    }

    view(): void
    {
        this.$metadataActionService.ExecuteAction(null, this.check().ViewAction, new RepositoryFormData(null, null));
    }
}
